import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer } from 'react-toastify';
import useRoundup from '../../hooks/use-roundup';
import ScheduledGoalList from '../../partials/goals/scheduled-goal-list';
import Scorecard from '../../partials/roundups/underway/scorecard';
import WorkItems from '../../partials/roundups/underway/work-items';
import Finalise from '../../partials/roundups/underway/finalise';

const stages = ['Scorecard', 'Review goals', 'Issues', 'Tasks', 'Summary'];

export default function Roundup_Underway() {
  const roundup = useRoundup();
  const [stage, setStage] = useState(0);
  const [issues, setIssues] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const yearId = roundup.year?._id;
  const number = roundup.number;
  const roundupId = roundup.roundup?._id;
  const context = useMemo(() => ({ year_id: yearId, roundup_number: number }), [yearId, number]);
  const refreshWork = useCallback(async () => {
    if (!roundupId) return;
    setFetching(true);
    try {
      const [issueResponse, taskResponse] = await Promise.all([
        axios.post(`${process.env.REACT_APP_WEB_URL}api/roundups/underway/get-scorecard-issues`, context, { withCredentials: true }),
        axios.post(`${process.env.REACT_APP_WEB_URL}api/roundups/underway/get-scorecard-tasks`, context, { withCredentials: true })
      ]);
      if (!Array.isArray(issueResponse.data.fetchedScorecardIssues) || !Array.isArray(taskResponse.data.fetchedScorecardTasks)) throw new Error();
      setIssues(issueResponse.data.fetchedScorecardIssues); setTasks(taskResponse.data.fetchedScorecardTasks); setError('');
    } catch { setError('Unable to load your issues and tasks. Please try again.'); throw new Error('Unable to refresh your work.'); }
    finally { setFetching(false); }
  }, [roundupId, context]);
  useEffect(() => { refreshWork().catch(() => {}); }, [refreshWork]);
  useEffect(() => { if (roundup.isCompleted) setStage(4); }, [roundup.isCompleted]);
  const taskParents = useMemo(() => [...new Map([...issues, ...tasks.map(task => task.parent_issue_id).filter(Boolean)].map(issue => [issue._id, issue])).values()], [issues, tasks]);
  const next = async () => {
    try { await roundup.waitForSave(); setStage(previous => Math.min(4, previous + 1)); }
    catch { /* Save error is displayed above the stages. */ }
  };

  if (roundup.loading) return <p role="status">Loading your roundup…</p>;
  if (roundup.error) return <div role="alert">{roundup.error} <button onClick={roundup.retry}>Try again</button></div>;
  if (!roundup.year) return <div><h1>Start your year first</h1><Link to="/manage-year">Manage your year</Link></div>;
  if (!roundup.roundup) return <div><h1>Prepare your roundup first</h1><Link to="/prepare-weekly-roundup">Plan Roundup {roundup.number}</Link></div>;

  const blocked = busy || fetching || roundup.saving || !!roundup.saveError || !!error;
  return <>
    <div className="roundup-underway">
      <h1 className="text-align--center">Roundup {roundup.number}{roundup.isCompleted ? ' complete' : ' underway'}</h1>
      {roundup.isCompleted && <p className="roundup-completed-notice">Your completed roundup is saved and locked. You can download its summary below.</p>}
      {roundup.saveError && <p role="alert">{roundup.saveError} <button onClick={() => roundup.persist().catch(() => {})}>Try saving again</button></p>}
      {error && <p role="alert">{error} <button onClick={() => refreshWork().catch(() => {})}>Try again</button></p>}
      <ol className="roundup-stages" aria-label="Roundup stages">{stages.map((label, index) => <li key={label} className={stage === index ? 'active' : index < stage ? 'completed' : ''} aria-current={stage === index ? 'step' : undefined}>{label}</li>)}</ol>
      {(roundup.saving || fetching) && <p role="status">{roundup.saving ? 'Saving your roundup…' : 'Loading your issues and tasks…'}</p>}
      <div className="roundup-stage-content">
        {stage === 0 && <Scorecard context={context} items={roundup.items} onItemsChange={roundup.changeItems} isCompleted={roundup.isCompleted} />}
        {stage === 1 && <div><h2>Review your goals</h2><p>Update progress, mark finished goals complete, or move a goal to another quarter before reviewing your blockers.</p>
          <ScheduledGoalList goals={roundup.goals} refreshGoals={roundup.updateGoal} readOnly={roundup.isCompleted} />
          {!roundup.goals.length && <p>No goals selected. <Link to="/prepare-weekly-roundup">Choose your roundup goals</Link></p>}
        </div>}
        {stage === 2 && <WorkItems kind="issue" items={issues} parents={roundup.goals} context={context} refresh={refreshWork} isCompleted={roundup.isCompleted} onBusyChange={setBusy} />}
        {stage === 3 && <WorkItems kind="task" items={tasks} parents={taskParents} context={context} refresh={refreshWork} isCompleted={roundup.isCompleted} onBusyChange={setBusy} />}
        {stage === 4 && <Finalise roundup={roundup} issues={issues} tasks={tasks} blocked={blocked} />}
      </div>
      <div className="stage-navigation-button-wrap">
        {stage > 0 && <button className="prev" disabled={busy || roundup.saving} onClick={() => setStage(previous => previous - 1)}>Previous Stage</button>}
        {stage < 4 && <button className="next" disabled={blocked} onClick={next}>Next Stage</button>}
        <Link to="/">Back to Home</Link>
      </div>
    </div>
    <ToastContainer />
  </>;
}
